import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { makeApiCall } from '../../config/api';
import { formatScheduledDate, todayLocalYmd } from '../../utils/dateUtils';
import {
  buildContractedServiceItems,
  isServiceChecked,
} from '../../utils/contractedServiceItems';

const STATUS_COLORS = {
  completed: 'success',
  in_progress: 'warning',
  cancelled: 'error',
  scheduled: 'default',
};

const minutesBetween = (start, end) => {
  if (!start || !end) return null;
  const diff = (new Date(end) - new Date(start)) / 60000;
  return Number.isFinite(diff) && diff >= 0 ? Math.round(diff) : null;
};

const SummerJobReport = () => {
  const { t, i18n } = useTranslation();
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return todayLocalYmd(d);
  });
  const [toDate, setToDate] = useState(() => todayLocalYmd());
  // TKT-452: Naomi wanted the report pulled together by truck.
  const [groupBy, setGroupBy] = useState('truck');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [generatingExcel, setGeneratingExcel] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      const response = await makeApiCall(`api/reports/summer-jobs?${params}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setData(result.data);
        } else {
          setError(t('reports.error.fetching'));
        }
      } else {
        setError(t('reports.error.fetching'));
      }
    } catch (err) {
      console.error('Error fetching summer jobs report:', err);
      setError(t('reports.error.fetching'));
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (kind) => {
    const setBusy = kind === 'pdf' ? setGeneratingPDF : setGeneratingExcel;
    setBusy(true);
    setError(null);
    try {
      const response = await makeApiCall(`api/reports/summer-jobs/${kind}`, {
        method: 'POST',
        body: JSON.stringify({
          from_date: fromDate || null,
          to_date: toDate || null,
          group_by: groupBy,
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `summer-jobs-report-${Date.now()}.${kind}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // TKT-251: surface the actual server message for 413
        // (PDF_ROW_CAP_EXCEEDED) so the user knows to narrow their
        // date range. Other errors fall back to the generic message.
        const body = await response.json().catch(() => ({}));
        setError(body.error || t(kind === 'pdf' ? 'reports.error.pdf' : 'reports.error.excel'));
      }
    } catch (err) {
      console.error(`Error generating ${kind}:`, err);
      setError(t(kind === 'pdf' ? 'reports.error.pdf' : 'reports.error.excel'));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatTime = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString(i18n.language, {
      timeZone: 'America/Toronto',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDay = (value) => formatScheduledDate(value, i18n.language, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }) || '-';

  const customerName = (job) => job.company_name
    || `${job.first_name || ''} ${job.last_name || ''}`.trim()
    || t('reports.summerJobs.unknownCustomer');

  // Services the crew checked off at clock-out, labelled the same way as
  // the crew's checklist.
  const servicesDone = (job) => {
    if (!job.checked_services) return [];
    return buildContractedServiceItems(job.available_services, { t, jobType: 'summer' })
      .filter((item) => isServiceChecked(job.checked_services, item.key))
      .map((item) => item.label);
  };

  const groups = useMemo(() => {
    const map = new Map();
    data.forEach((job) => {
      const key = groupBy === 'truck'
        ? (job.truck_id ? `truck-${job.truck_id}` : 'truck-none')
        : `customer-${job.contact_id || 'unknown'}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          title: groupBy === 'truck'
            ? (job.truck_name || t('reports.summerJobs.noTruck'))
            : customerName(job),
          subtitle: groupBy === 'truck'
            ? ''
            : [job.street_no, job.city, job.postal_code].filter(Boolean).join(', '),
          jobs: [],
        });
      }
      map.get(key).jobs.push(job);
    });
    const list = Array.from(map.values());
    list.forEach((g) => {
      g.jobs.sort((a, b) => String(b.scheduled_date).localeCompare(String(a.scheduled_date))
        || String(a.clock_in_time || '').localeCompare(String(b.clock_in_time || '')));
    });
    return list.sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, groupBy, t]);

  const completedCount = data.filter((job) => job.status === 'completed').length;
  const uniqueCustomers = new Set(data.map((job) => job.contact_id || 'unknown')).size;
  const uniqueTrucks = new Set(data.map((job) => job.truck_id || 'none')).size;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('reports.summerJobs.explainer')}
      </Alert>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          {t('reports.filters')}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label={t('reports.fromDate')}
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label={t('reports.toDate')}
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {t('reports.summerJobs.groupBy')}
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={groupBy}
                onChange={(_, value) => value && setGroupBy(value)}
                aria-label={t('reports.summerJobs.groupBy')}
              >
                <ToggleButton value="truck">{t('reports.summerJobs.byTruck')}</ToggleButton>
                <ToggleButton value="customer">{t('reports.summerJobs.byCustomer')}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={fetchReport}
                disabled={loading || !fromDate || !toDate}
                sx={{ bgcolor: '#2e7d32' }}
              >
                {t('reports.load')}
              </Button>
              <Button
                variant="contained"
                startIcon={generatingPDF ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={() => downloadFile('pdf')}
                disabled={generatingPDF || data.length === 0}
                sx={{ bgcolor: '#d32f2f' }}
                aria-label={t('reports.downloadPDF')}
              >
                {t('reports.downloadPDF')}
              </Button>
              <Button
                variant="contained"
                startIcon={generatingExcel ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={() => downloadFile('xlsx')}
                disabled={generatingExcel || data.length === 0}
                sx={{ bgcolor: '#1d6f42' }}
                aria-label={t('reports.downloadExcel')}
              >
                {t('reports.downloadExcel')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {data.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: '#e8f5e9' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
              {t('reports.summary')}
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Chip label={`${t('reports.totalJobs')}: ${data.length}`} color="primary" />
              <Chip label={t('reports.summerJobs.completedCount', { count: completedCount })} color="success" />
              <Chip label={`${t('reports.uniqueCustomers')}: ${uniqueCustomers}`} color="secondary" />
              <Chip label={t('reports.summerJobs.truckCount', { count: uniqueTrucks })} />
            </Stack>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : data.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {t('reports.noData')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('reports.selectDateRange')}
          </Typography>
        </Paper>
      ) : (
        <Box>
          {groups.map((group) => {
            const done = group.jobs.filter((job) => job.status === 'completed').length;
            return (
              <Card key={group.key} sx={{ mb: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: group.subtitle ? 0.5 : 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                      {group.title}
                    </Typography>
                    <Chip
                      size="small"
                      label={t('reports.summerJobs.groupCount', { done, total: group.jobs.length })}
                    />
                  </Stack>
                  {group.subtitle && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {group.subtitle}
                    </Typography>
                  )}

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell><strong>{t('reports.date')}</strong></TableCell>
                          {groupBy === 'truck' ? (
                            <TableCell><strong>{t('reports.customer')}</strong></TableCell>
                          ) : (
                            <TableCell><strong>{t('reports.summerJobs.truck')}</strong></TableCell>
                          )}
                          <TableCell><strong>{t('reports.clockIn')}</strong></TableCell>
                          <TableCell><strong>{t('reports.clockOut')}</strong></TableCell>
                          <TableCell><strong>{t('reports.summerJobs.timeOnSite')}</strong></TableCell>
                          <TableCell><strong>{t('reports.summerJobs.allotted')}</strong></TableCell>
                          <TableCell><strong>{t('reports.status')}</strong></TableCell>
                          <TableCell><strong>{t('reports.summerJobs.servicesDone')}</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.jobs.map((job) => {
                          const onSite = minutesBetween(job.clock_in_time, job.clock_out_time);
                          const allotted = Number(job.allotted_minutes) || null;
                          const services = servicesDone(job);
                          return (
                            <TableRow key={job.id}>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDay(job.scheduled_date)}</TableCell>
                              {groupBy === 'truck' ? (
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{customerName(job)}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {[job.street_no, job.city].filter(Boolean).join(', ')}
                                  </Typography>
                                </TableCell>
                              ) : (
                                <TableCell>{job.truck_name || t('reports.summerJobs.noTruck')}</TableCell>
                              )}
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTime(job.clock_in_time)}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTime(job.clock_out_time)}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {onSite == null ? '-' : t('reports.summerJobs.minutes', { count: onSite })}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {allotted == null ? '-' : t('reports.summerJobs.minutes', { count: allotted })}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={t(`reports.summerJobs.status.${job.status}`, job.status || '-')}
                                  size="small"
                                  color={STATUS_COLORS[job.status] || 'default'}
                                />
                              </TableCell>
                              <TableCell>{services.length ? services.join(', ') : '-'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default SummerJobReport;
